import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  StrategyFeed,
  ScreenSummary,
  SignalEmission,
  PlatformApiError,
  listFeeds,
  getFeed,
  setFeedEnabled,
  listScreens,
  listEmissions,
} from '../services/platformApi';

const CREATE_COMMAND =
  'propalgos feeds create --strategy statarb-perps --name "My feed" \\\n' +
  '  --execution-url https://your-endpoint.example/signals --cadence 1h';

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
};

const formatLatency = (ms: number | null): string =>
  ms === null || ms === undefined ? '—' : `${Math.round(ms)} ms`;

/** Enough hex to eyeball against a receiver-side digest, short enough for a table. */
const shortSha = (sha: string | null | undefined): string =>
  sha ? sha.slice(0, 10) : '—';

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: 'green' | 'red' | 'amber' | 'gray' | 'blue';
}) {
  const tones: Record<string, string> = {
    green: 'bg-green-500/20 text-green-400',
    red: 'bg-red-500/20 text-red-400',
    amber: 'bg-amber-500/20 text-amber-300',
    gray: 'bg-gray-700 text-gray-300',
    blue: 'bg-blue-500/20 text-blue-400',
  };
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${tones[tone]}`}>
      {children}
    </span>
  );
}

function EmptyFeeds() {
  return (
    <div className="bg-gray-900 rounded-xl border border-gray-800 p-10 text-center">
      <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 11a9 9 0 019 9M4 4a16 16 0 0116 16M6 19a1 1 0 100-2 1 1 0 000 2z" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-white mb-2">No signal feeds yet</h3>
      <p className="text-gray-400 mb-6 max-w-xl mx-auto">
        Feeds are created from the CLI so the signing secret is provisioned into Vault
        at the same moment the feed row is written. Create one, then come back here to
        watch its screens and delivery audit trail.
      </p>
      <pre className="text-left bg-black/60 border border-gray-800 rounded-lg p-4 max-w-2xl mx-auto overflow-x-auto">
        <code className="text-sm text-gray-300 font-mono">{CREATE_COMMAND}</code>
      </pre>
      <p className="text-gray-500 text-sm mt-4">
        Needs an API key with the{' '}
        <code className="bg-gray-800 px-1.5 py-0.5 rounded text-gray-300">feeds:write</code>{' '}
        scope — create one on the{' '}
        <a href="/dashboard/api-keys" className="text-blue-400 hover:text-blue-300 underline">
          API Keys
        </a>{' '}
        page.
      </p>
    </div>
  );
}

export default function SignalFeedsPage() {
  const navigate = useNavigate();

  const [feeds, setFeeds] = useState<StrategyFeed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedFeedId, setSelectedFeedId] = useState<string | null>(null);
  const [screens, setScreens] = useState<ScreenSummary[]>([]);
  const [emissions, setEmissions] = useState<SignalEmission[]>([]);
  // Server-side totals: both lists are fetched with limit=50, so the count of
  // rows on screen is not the count of rows that exist.
  const [screensTotal, setScreensTotal] = useState(0);
  const [emissionsTotal, setEmissionsTotal] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // 403 counts as an auth failure alongside 401: an unauthenticated request
  // can answer either (HTTPBearer vs. the account-status check), and neither
  // is something a Retry button in this page can resolve.
  const handleAuthError = useCallback(
    (err: unknown): boolean => {
      if (err instanceof PlatformApiError && (err.status === 401 || err.status === 403)) {
        navigate('/login');
        return true;
      }
      return false;
    },
    [navigate]
  );

  // The list serializer returns `latest_screen: null` for every row, so the
  // summary each card shows has to come from a per-feed GET. Hydrating here
  // (inside the one load) rather than in a second effect keyed on `feeds`
  // keeps this from re-triggering itself on every state write.
  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const page = await listFeeds(signal);
        if (signal?.aborted) return;
        setFeeds(page.items);
        setError(null);

        const hydrated = await Promise.all(
          page.items.map(async (feed) => {
            try {
              return await getFeed(feed.feed_id, signal);
            } catch (err) {
              // A session that expired mid-hydration must bounce, not quietly
              // render every feed as "no screens yet" — that reads as real
              // data. Rethrow so the outer catch redirects.
              if (
                err instanceof PlatformApiError &&
                (err.status === 401 || err.status === 403)
              ) {
                throw err;
              }
              // Any other per-feed failure still lists the feed; it just shows
              // no latest screen rather than taking the whole page down.
              return feed;
            }
          })
        );
        if (signal?.aborted) return;
        setFeeds(hydrated);
      } catch (err) {
        if (signal?.aborted) return;
        if (handleAuthError(err)) return;
        setError(err instanceof Error ? err.message : 'Failed to load signal feeds');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [handleAuthError]
  );

  useEffect(() => {
    if (!localStorage.getItem('authToken')) {
      navigate('/login');
      return;
    }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, navigate]);

  // Detail pane: one effect, keyed only on the selected id.
  useEffect(() => {
    if (!selectedFeedId) {
      setScreens([]);
      setEmissions([]);
      setScreensTotal(0);
      setEmissionsTotal(0);
      return;
    }
    const controller = new AbortController();
    setDetailLoading(true);
    setDetailError(null);

    Promise.all([
      listScreens(selectedFeedId, controller.signal),
      listEmissions(selectedFeedId, controller.signal),
    ])
      .then(([screenPage, emissionPage]) => {
        if (controller.signal.aborted) return;
        setScreens(screenPage.items);
        setEmissions(emissionPage.items);
        setScreensTotal(screenPage.total);
        setEmissionsTotal(emissionPage.total);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (handleAuthError(err)) return;
        setDetailError(err instanceof Error ? err.message : 'Failed to load feed detail');
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });

    return () => controller.abort();
  }, [selectedFeedId, handleAuthError]);

  const handleToggle = async (feed: StrategyFeed) => {
    setTogglingId(feed.feed_id);
    try {
      const updated = await setFeedEnabled(feed.feed_id, !feed.enabled);
      setFeeds((prev) =>
        prev.map((f) => (f.feed_id === updated.feed_id ? { ...f, ...updated } : f))
      );
      setError(null);
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err instanceof Error ? err.message : 'Failed to update feed');
    } finally {
      setTogglingId(null);
    }
  };

  const selectedFeed = feeds.find((f) => f.feed_id === selectedFeedId) || null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Signal Feeds</h1>
          <p className="text-gray-400 max-w-3xl">
            Each feed screens a universe on a cadence and POSTs HMAC-signed emissions to
            your execution endpoint. Every send is recorded once and only once — this page
            is that audit trail.
          </p>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4 mb-6 flex items-center justify-between">
            <span className="text-red-300 text-sm">{error}</span>
            <button
              onClick={() => load()}
              className="px-3 py-1.5 bg-red-800/50 hover:bg-red-800 text-white rounded-lg text-sm transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {feeds.length === 0 ? (
          <EmptyFeeds />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Master: feed list */}
            <div className="lg:col-span-1 space-y-3">
              {feeds.map((feed) => {
                const selected = feed.feed_id === selectedFeedId;
                return (
                  <div
                    key={feed.feed_id}
                    className={`rounded-xl border p-5 transition-colors cursor-pointer ${
                      selected
                        ? 'bg-gray-800 border-gray-600'
                        : 'bg-gray-900 border-gray-800 hover:border-gray-700'
                    }`}
                    onClick={() => setSelectedFeedId(selected ? null : feed.feed_id)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-base font-semibold text-white truncate mr-2">
                        {feed.name}
                      </h3>
                      <Badge tone={feed.enabled ? 'green' : 'gray'}>
                        {feed.enabled ? 'enabled' : 'disabled'}
                      </Badge>
                    </div>

                    <div className="text-xs text-gray-500 font-mono mb-3 truncate">
                      {feed.feed_id}
                    </div>

                    <div className="space-y-1 text-sm text-gray-400">
                      <div>
                        Strategy:{' '}
                        <span className="text-gray-300 font-mono">{feed.strategy_id}</span>
                      </div>
                      <div>
                        Cadence: <span className="text-gray-300">{feed.cadence}</span>
                      </div>
                      <div className="pt-1">
                        {feed.latest_screen ? (
                          <span>
                            Latest screen:{' '}
                            <span className="text-gray-300">
                              {feed.latest_screen.n_survivors} survivors
                            </span>{' '}
                            through {formatDateTime(feed.latest_screen.data_through)}
                          </span>
                        ) : (
                          <span className="text-gray-500">No screens uploaded yet</span>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggle(feed);
                        }}
                        disabled={togglingId === feed.feed_id}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          feed.enabled
                            ? 'bg-gray-700 hover:bg-gray-600 text-white'
                            : 'bg-white hover:bg-gray-100 text-black'
                        }`}
                      >
                        {togglingId === feed.feed_id
                          ? 'Saving…'
                          : feed.enabled
                          ? 'Disable'
                          : 'Enable'}
                      </button>
                      <span className="text-xs text-gray-500">
                        {selected ? 'Hide detail' : 'View detail'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Detail */}
            <div className="lg:col-span-2">
              {!selectedFeed ? (
                <div className="bg-gray-900 rounded-xl border border-gray-800 p-10 text-center h-full flex flex-col items-center justify-center">
                  <h3 className="text-lg font-medium text-white mb-2">Select a feed</h3>
                  <p className="text-gray-400 max-w-md">
                    Pick a feed to see its pair screens and the full delivery audit trail —
                    every emission, whether it was a dry run, and what your endpoint answered.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Feed summary */}
                  <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="min-w-0">
                        <h2 className="text-xl font-bold text-white truncate">
                          {selectedFeed.name}
                        </h2>
                        <div className="text-xs text-gray-500 font-mono mt-1">
                          {selectedFeed.feed_id}
                        </div>
                      </div>
                      <Badge tone={selectedFeed.enabled ? 'green' : 'gray'}>
                        {selectedFeed.enabled ? 'enabled' : 'disabled'}
                      </Badge>
                    </div>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                      <div>
                        <dt className="text-gray-500">Execution endpoint</dt>
                        <dd className="text-gray-300 font-mono break-all">
                          {selectedFeed.execution_url}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Signing key id</dt>
                        <dd className="text-gray-300 font-mono">{selectedFeed.key_id}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Cadence</dt>
                        <dd className="text-gray-300">{selectedFeed.cadence}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Created</dt>
                        <dd className="text-gray-300">
                          {formatDateTime(selectedFeed.created_at)}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  {detailError && (
                    <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4">
                      <span className="text-red-300 text-sm">{detailError}</span>
                    </div>
                  )}

                  {detailLoading ? (
                    <div className="bg-gray-900 rounded-xl border border-gray-800 p-10 flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                  ) : (
                    <>
                      {/* Screens */}
                      <div className="bg-gray-900 rounded-xl border border-gray-800">
                        <div className="p-5 border-b border-gray-800 flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-white">Pair screens</h3>
                          <span className="text-sm text-gray-500">
                            {screens.length} of {screensTotal}
                          </span>
                        </div>
                        {screens.length === 0 ? (
                          <div className="p-6 text-sm text-gray-400">
                            No screens uploaded. The weekly GPU screen job POSTs a{' '}
                            <code className="bg-gray-800 px-1.5 py-0.5 rounded text-gray-300">
                              PairScreen
                            </code>{' '}
                            to this feed; until it runs there is nothing to rank against.
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b border-gray-800">
                                  <th className="px-5 py-3 font-medium">Data through</th>
                                  <th className="px-5 py-3 font-medium">Survivors</th>
                                  <th className="px-5 py-3 font-medium">Screen id</th>
                                </tr>
                              </thead>
                              <tbody>
                                {screens.map((screen) => (
                                  <tr
                                    key={screen.screen_id}
                                    className="border-b border-gray-800/50 last:border-0"
                                  >
                                    <td className="px-5 py-3 text-gray-300">
                                      {formatDateTime(screen.data_through)}
                                    </td>
                                    <td className="px-5 py-3 text-gray-300">
                                      {screen.n_survivors}
                                    </td>
                                    <td className="px-5 py-3 text-gray-500 font-mono text-xs">
                                      {screen.screen_id}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {/* Emissions audit */}
                      <div className="bg-gray-900 rounded-xl border border-gray-800">
                        <div className="p-5 border-b border-gray-800 flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-white">Emissions</h3>
                          <span className="text-sm text-gray-500">
                            {emissions.length} of {emissionsTotal}
                          </span>
                        </div>
                        {emissions.length === 0 ? (
                          <div className="p-6 text-sm text-gray-400">
                            No emissions recorded. An emission comes from running one
                            cycle: compute it locally with{' '}
                            <code className="bg-gray-800 px-1.5 py-0.5 rounded text-gray-300">
                              propalgos.signals.emission.run_cycle
                            </code>{' '}
                            and upload the result with{' '}
                            <code className="bg-gray-800 px-1.5 py-0.5 rounded text-gray-300">
                              Client.upload_emission
                            </code>
                            . Server-side runs are not available yet.{' '}
                            <code className="bg-gray-800 px-1.5 py-0.5 rounded text-gray-300">
                              propalgos agent-guide --workflow run-feed
                            </code>{' '}
                            walks through the current path end to end.
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-gray-500 border-b border-gray-800">
                                  <th className="px-5 py-3 font-medium">Time</th>
                                  <th className="px-5 py-3 font-medium">Emission</th>
                                  <th className="px-5 py-3 font-medium">Mode</th>
                                  <th className="px-5 py-3 font-medium">Delivered</th>
                                  <th className="px-5 py-3 font-medium">Status</th>
                                  <th className="px-5 py-3 font-medium">Latency</th>
                                  <th className="px-5 py-3 font-medium">Attempts</th>
                                  <th className="px-5 py-3 font-medium">Payload sha256</th>
                                </tr>
                              </thead>
                              <tbody>
                                {emissions.map((em) => (
                                  <tr
                                    key={em.emission_id}
                                    className="border-b border-gray-800/50 last:border-0"
                                  >
                                    <td className="px-5 py-3 text-gray-300 whitespace-nowrap">
                                      {formatDateTime(em.created_at)}
                                    </td>
                                    <td className="px-5 py-3 text-gray-400 font-mono text-xs">
                                      {em.emission_id}
                                    </td>
                                    <td className="px-5 py-3">
                                      {em.dry_run ? (
                                        <Badge tone="amber">dry run</Badge>
                                      ) : (
                                        <Badge tone="blue">live</Badge>
                                      )}
                                    </td>
                                    <td className="px-5 py-3">
                                      {em.dry_run ? (
                                        <span className="text-gray-500">—</span>
                                      ) : em.delivered ? (
                                        <span className="text-green-400" title="delivered">
                                          ✓
                                        </span>
                                      ) : (
                                        <span className="text-red-400" title="not delivered">
                                          ✗
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-5 py-3 text-gray-300">
                                      {em.response_status ?? '—'}
                                    </td>
                                    <td className="px-5 py-3 text-gray-300 whitespace-nowrap">
                                      {formatLatency(em.latency_ms)}
                                    </td>
                                    <td className="px-5 py-3 text-gray-300">
                                      {em.attempts?.length ?? 0}
                                    </td>
                                    <td className="px-5 py-3">
                                      <code
                                        className="bg-gray-800 text-gray-400 px-2 py-1 rounded text-xs font-mono"
                                        title={em.payload_sha256 || undefined}
                                      >
                                        {shortSha(em.payload_sha256)}
                                      </code>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
